import styled from '@emotion/styled';
import P2 from '@base/P2/P2';
import Button from '@base/Button/Button';
import Paper from '@base/Paper/Paper';
import { Fonts, Spacings } from '@styles/index';
import Image from '@base/Image';
import { GTP_TEST_SERIES_TYPE_MAP } from '@constants/index';
import { enrollInQuiz } from '@actions/quizTaking';
import { useToggleable } from '@stores/toggleables';
import { useSnackbar } from '@stores/snackbar';
import { LOGIN_DRAWER } from '@constants/toggleables';
import {
  getRegularDate,
  getTimeFormatInShortFormWithoutSecsFromMins
} from '@utils/dates';
import pushRoute from '@utils/pushRoute';
import { useDispatch, useSelector } from 'react-redux';
import { getIsLoggedIn } from '@selectors/auth';
import { getIsDarkModeOn } from '@stores/selectors/darkModeStatusSelectors';
import useDarkModeStatus from '@stores/useDarkModeStatus';
import useTestSegmentAnalytics from '@cont/SeeAll/TestSeriesListing/useTestSegmentAnalytics';
import { ENGLISH_PREFRENCE } from '@constants/quiz';
import { emptyObject } from '@constants/empty';
import { TEST_DETAILS_VIEWED, TEST_STARTED } from '@constants/segment';

const OuterWrapper = styled(Paper)`
  background-color: var(--color-base-1);
  margin-bottom: ${Spacings.SPACING_4B};
  width: 416px;
  cursor: ${({ $isClickable }) => ($isClickable ? 'pointer' : 'auto')};
`;

const InfoWrapper = styled.div`
  display: flex;
  justify-content: space-between;
`;

const StyledButton = styled(Button)`
  border-radius: ${Spacings.SPACING_1B};
  font-size: ${Fonts.SMALL};
  height: 40px;
  color: ${({ $isDarkModeOn, $textColor }) =>
    $isDarkModeOn ? $textColor : ''};
`;

const ActionWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: ${Spacings.SPACING_5B};
  height: ${Spacings.SPACING_14B};
  margin-top: ${Spacings.SPACING_4B};
  border-top: solid 1px var(--color-divider);
`;

const Label = styled.div`
  font-size: ${Fonts.EXTRA_SMALL};
  color: ${({ $color }) => $color};
  font-weight: ${Fonts.HEAVY};
`;

const StyledH5 = styled.div`
  white-space: nowrap;
  width: 320px;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-bottom: ${Spacings.SPACING_2B};
  margin-top: ${Spacings.SPACING_1B};
  font-size: ${Spacings.SPACING_4B};
  font-weight: ${Fonts.SEMIBOLD};
  color: var(--color-text-primary);
`;

const getBtnVariant = ({ testType, attemptCount, isSubscribed, isOngoing }) => {
  if (isOngoing) {
    return 'hollow';
  }
  if (testType === 'FREE' && attemptCount === 0) {
    return 'filled';
  }
  if (testType === GTP_TEST_SERIES_TYPE_MAP.SUBSCRIPTION) {
    if (isSubscribed && attemptCount === 0) {
      return 'filled';
    }
  }
  return 'hollow';
};

const getBtnTextColor = ({
  testType,
  attemptCount,
  isSubscribed,
  isOngoing
}) => {
  if (isOngoing) {
    return 'var(--color-i-gray-9)';
  }
  if (testType === 'FREE' && attemptCount === 0) {
    return 'var(--color-i-black)';
  }
  if (testType === GTP_TEST_SERIES_TYPE_MAP.SUBSCRIPTION) {
    if (isSubscribed && attemptCount === 0) {
      return 'var(--color-i-black)';
    }
  }
  return 'var(--color-i-gray-9)';
};

const getTextBasedOnAttemptCount = (attemptCount, date, isOngoing) => {
  const formattedDate = getRegularDate(date);
  if (isOngoing) {
    return `Started attempting on ${formattedDate}`;
  }
  if (attemptCount === 0) {
    return 'Start your attempt now';
  }
  if (attemptCount === 1) {
    return `Attempted on ${formattedDate}`;
  }
  if (attemptCount > 1) {
    return `Last attempted on ${formattedDate}`;
  }
};

const getButtonCta = (attemptCount, isOngoing) => {
  if (isOngoing) {
    return 'Resume';
  }
  if (attemptCount === 0) {
    return 'Attempt';
  }
  if (attemptCount === 1) {
    return 'View results';
  }
  if (attemptCount > 1) {
    return 'View attempts';
  }
};

const getTestCardDetails = ({
  testType,
  attemptCount,
  isSubscribed,
  startTime,
  lastAttempt,
  isOngoing
}) => {
  if (testType === GTP_TEST_SERIES_TYPE_MAP.FREE) {
    return {
      label: 'FREE TEST',
      labelColor: 'var(--color-i-blue-25)',
      iconSrc: '/gtp/free-test.svg',
      infoString: getTextBasedOnAttemptCount(
        attemptCount,
        lastAttempt,
        isOngoing
      ),
      cta: getButtonCta(attemptCount, isOngoing)
    };
  }
  if (testType === GTP_TEST_SERIES_TYPE_MAP.SUBSCRIPTION) {
    if (isSubscribed) {
      return {
        label: 'PLUS TEST',
        labelColor: 'var(--color-i-accent-green)',
        iconSrc: '/gtp/free-test.svg',
        infoString: getTextBasedOnAttemptCount(
          attemptCount,
          lastAttempt,
          isOngoing
        ),
        cta: getButtonCta(attemptCount, isOngoing)
      };
    }
    return {
      label: 'AVAILABLE WITH SUBSCRIPTION',
      labelColor: 'var(--color-i-accent-green)',
      iconSrc: '/gtp/test-with-subscription.svg',
      infoString: 'Attempt with subscription',
      cta: 'View details'
    };
  }
  if (testType === GTP_TEST_SERIES_TYPE_MAP.COMING_SOON) {
    const formattedDate = getRegularDate(startTime);
    return {
      label: 'COMING SOON',
      labelColor: 'var(--color-i-gray-13)',
      iconSrc: '/gtp/upcoming-test.svg',
      infoString: `Launching on ${formattedDate}`,
      cta: 'View details'
    };
  }
  return emptyObject;
};

const GtpTestSeriesCard = ({ test, isSubscribed }) => {
  const isDarkModeOn = useDarkModeStatus(getIsDarkModeOn);
  const {
    viewType: testType,
    title = '',
    uid = '',
    startTime = null,
    duration,
    numQuestions: noOfQuestions,
    testSeriesSlug: slug = '',
    isSpecial,
    sessionDetails = {}
  } = test;
  const {
    attemptCount,
    uid: quizSessionUID,
    lastAttempt = null,
    isOngoing
  } = sessionDetails;

  const {
    label = '',
    labelColor = '',
    iconSrc = '',
    infoString = '',
    cta = ''
  } = getTestCardDetails({
    testType,
    attemptCount,
    isSubscribed,
    startTime,
    lastAttempt,
    isOngoing
  });

  const dispatch = useDispatch();
  const { open: showSnackbar } = useSnackbar();
  const { openElement: openLoginDrawer } = useToggleable(LOGIN_DRAWER);
  const isLoggedIn = useSelector(getIsLoggedIn);
  const segmentAnalytics = useTestSegmentAnalytics(testType, isSpecial);

  const enrollUserInQuiz = async () => {
    const { error } = await dispatch(enrollInQuiz(uid, ENGLISH_PREFRENCE.code));
    if (error) {
      return showSnackbar({
        message: error.message || 'Unable to start the test. Please try again!',
        type: 'error'
      });
    }
    return pushRoute(`/quiz/${slug}/${uid}`);
  };

  const handleButtonClick = async (event) => {
    event.stopPropagation();
    if (!isLoggedIn) {
      return openLoginDrawer();
    }
    if (cta === 'Attempt') {
      await enrollUserInQuiz();
      return segmentAnalytics(TEST_STARTED, {
        is_reattempt: false,
        language: ENGLISH_PREFRENCE.display_name,
        test_eligibility_type: isSpecial ? 'Free Test' : 'Plus Test'
      });
    }
    if (cta === 'Resume') {
      return enrollUserInQuiz();
    }
    if (cta === 'View details' || cta === 'View attempts') {
      segmentAnalytics(TEST_DETAILS_VIEWED, {
        ui_source: cta === 'View details' ? 'view_details' : 'view_attempts'
      });
      return pushRoute(`/quiz/${slug}/${uid}`);
    }
    if (cta === 'View results' && quizSessionUID) {
      return pushRoute(`/quiz/${slug}/${uid}/attempts/${quizSessionUID}`);
    }
  };

  const isCardClickable = () => {
    if (testType === GTP_TEST_SERIES_TYPE_MAP.COMING_SOON) {
      return false;
    }
    return true;
  };

  const handleCardClick = () => {
    if (isCardClickable()) {
      if (!isLoggedIn) {
        return openLoginDrawer();
      }
      segmentAnalytics(TEST_DETAILS_VIEWED, {
        ui_source: 'test_card'
      });
      pushRoute(`/quiz/${slug}/${uid}`);
    }
  };

  return (
    <OuterWrapper
      padding={Spacings.SPACING_6B}
      $isClickable={isCardClickable()}
      onClick={handleCardClick}
    >
      <InfoWrapper>
        <div>
          <Label $color={labelColor}>{label}</Label>
          <StyledH5>{title}</StyledH5>
          <P2 color="var(--color-text-secondary)">
            {noOfQuestions} questions •{' '}
            {getTimeFormatInShortFormWithoutSecsFromMins(
              duration / 60,
              false,
              'hr',
              'min'
            )}
          </P2>
        </div>
        <div>
          <Image width={36} height={36} alt="test" src={iconSrc} />
        </div>
      </InfoWrapper>
      <ActionWrapper>
        <P2 color="var(--color-text-secondary)">{infoString}</P2>
        <StyledButton
          variant={getBtnVariant({
            testType,
            attemptCount,
            isSubscribed,
            isOngoing
          })}
          color="black"
          label={cta}
          width="120px"
          maxHeight="sm"
          $isDarkModeOn={isDarkModeOn}
          $textColor={getBtnTextColor({
            testType,
            attemptCount,
            isSubscribed,
            isOngoing
          })}
          onClick={handleButtonClick}
        />
      </ActionWrapper>
    </OuterWrapper>
  );
};

export default GtpTestSeriesCard;
